import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Share2, Download } from 'lucide-react';
import confetti from 'canvas-confetti';
import html2canvas from 'html2canvas';
import { wrappedTranslations } from '../../../translations/wrapped';
import ShareableImage from '../ShareableImage';

interface FinaleCardProps {
    name: string;
    club: string;
    classification: string;
    totalGames: number;
    winPercentage: number;
    peakElo: number;
    bestStreak: number;
    lang: 'en' | 'fr' | 'de' | 'it';
}

const FinaleCard: React.FC<FinaleCardProps> = ({ name, club, classification, totalGames, winPercentage, peakElo, bestStreak, lang }) => {
    const t = wrappedTranslations[lang];
    const shareableImageRef = useRef<HTMLDivElement>(null);
    const [isGenerating, setIsGenerating] = useState(false);

    // Safely handle win percentage
    const safeWinPercentage = isNaN(winPercentage) ? 0 : Math.min(100, winPercentage);

    useEffect(() => {
        const duration = 1500;
        const end = Date.now() + duration;

        const frame = () => {
            confetti({
                particleCount: 2,
                angle: 60,
                spread: 55,
                origin: { x: 0 },
                colors: ['#1C7AD6', '#87CEFA', '#3b9eff']
            });
            confetti({
                particleCount: 2,
                angle: 120,
                spread: 55,
                origin: { x: 1 },
                colors: ['#1C7AD6', '#87CEFA', '#3b9eff']
            });

            if (Date.now() < end) {
                requestAnimationFrame(frame);
            }
        };

        frame();
    }, []);

    const handleShare = async () => {
        if (!shareableImageRef.current) return;

        setIsGenerating(true);
        try {
            // Generate image from the ShareableImage component
            const canvas = await html2canvas(shareableImageRef.current, {
                scale: 2, // Higher quality
                backgroundColor: '#f9fafb',
                logging: false,
                useCORS: true,
            });

            // Convert canvas to blob
            canvas.toBlob(async (blob) => {
                if (!blob) {
                    setIsGenerating(false);
                    return;
                }

                const file = new File([blob], 'ttstats-wrapped-2025.png', { type: 'image/png' });

                // Try Web Share API first (works on mobile)
                if (navigator.share && navigator.canShare?.({ files: [file] })) {
                    try {
                        await navigator.share({
                            title: 'My TTStats Wrapped 2025',
                            text: `Check out my Table Tennis year! I played ${totalGames} matches with a ${safeWinPercentage.toFixed(0)}% win rate.`,
                            files: [file],
                        });
                    } catch (err: any) {
                        // User cancelled or error occurred
                        if (err.name !== 'AbortError') {
                            console.error('Error sharing:', err);
                            downloadImage(canvas);
                        }
                    }
                } else {
                    // Fallback to download
                    downloadImage(canvas);
                }

                setIsGenerating(false);
            }, 'image/png');
        } catch (error) {
            console.error('Error generating image:', error);
            setIsGenerating(false);
        }
    };

    const downloadImage = (canvas: HTMLCanvasElement) => {
        const link = document.createElement('a');
        link.download = 'ttstats-wrapped-2025.png';
        link.href = canvas.toDataURL('image/png');
        link.click();
    };

    return (
        <div className="h-full w-full flex flex-col items-center justify-center text-center relative p-6">

            <div className="max-w-4xl w-full flex flex-col items-center relative z-10">
                {/* Preview of the shareable image */}
                <motion.div
                    className="mb-6 md:mb-8 rounded-2xl overflow-hidden shadow-2xl border-4 border-gray-200"
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.6 }}
                    style={{
                        width: '270px',
                        height: '480px'
                    }}
                >
                    <div style={{
                        transform: 'scale(0.25)',
                        transformOrigin: 'top left',
                        width: '1080px',
                        height: '1920px'
                    }}>
                        <ShareableImage
                            name={name}
                            club={club}
                            classification={classification}
                            totalGames={totalGames}
                            winPercentage={safeWinPercentage}
                            peakElo={peakElo}
                            bestStreak={bestStreak}
                            lang={lang}
                        />
                    </div>
                </motion.div>

                <motion.button
                    onClick={handleShare}
                    disabled={isGenerating}
                    className="flex items-center gap-3 px-8 py-4 bg-[#1C7AD6] hover:bg-[#1565C0] text-white rounded-full font-bold text-base md:text-lg shadow-lg transition-all transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                >
                    {isGenerating ? (
                        <>
                            <Download className="w-5 h-5 md:w-7 md:h-7 animate-pulse" />
                            Generating...
                        </>
                    ) : (
                        <>
                            <Share2 className="w-5 h-5 md:w-7 md:h-7" />
                            {t.shareYourYear}
                        </>
                    )}
                </motion.button>

                <motion.div
                    className="mt-6 md:mt-8 text-xs md:text-sm text-gray-500 font-medium tracking-widest"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8 }}
                >
                    #TTStatsWrapped
                </motion.div>
            </div>

            {/* Hidden ShareableImage for image generation */}
            <div className="fixed -left-[10000px] -top-[10000px] pointer-events-none">
                <ShareableImage
                    ref={shareableImageRef}
                    name={name}
                    club={club}
                    classification={classification}
                    totalGames={totalGames}
                    winPercentage={safeWinPercentage}
                    peakElo={peakElo}
                    bestStreak={bestStreak}
                    lang={lang}
                />
            </div>
        </div>
    );
};

export default FinaleCard;
